// The desk-tool tax invoice shared by the Fuel and Mileage calculators (audit DUP-22, owner 26 Sep 2026).
//
// Both calculators carried their own copy of the letterhead PDF (html2canvas + jsPDF), the Excel download and
// the "base x 0.05 VAT" block, and they had drifted: Fuel printed a hard-coded TRN and numbered ALWFQ-F-,
// Mileage read the TRN from the company settings and numbered ALWFQ-; Fuel's third column showed litres and
// Mileage's showed kilometres where the invoice needs the amount. Here one invoice: the seller TRN from the
// company settings, the number from the invoice-number service, and the line as Net / VAT 5% / Total (FIN-12).
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { saveAs } from 'file-saver';
import { netLine } from '../../../constants/finance';

/** Text for the invoice HTML: customer names and the like never become markup. */
export const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/** Net / VAT 5% / Total of a desk invoice, each rounded to fils (Total = Net + VAT as printed). */
export const deskInvoiceAmounts = (base) => netLine(base);

const fix2 = (n) => (Number(n) || 0).toFixed(2);

/**
 * The invoice page (HTML drawn on the letterhead). Every table cell is centred.
 * o.title        'Tax Invoice' / 'Fuel Tax Invoice'
 * o.subject      'SUB: ...'
 * o.greeting     lines above the table
 * o.description  lines of the description cell (HTML allowed - escape the data)
 * o.amountLabel  heading of the net amount column
 * o.amounts      {net, vat, total}
 */
export function deskInvoiceHtml({ title, date, ref, trn, subject, greeting = [], description = [], amountLabel = 'Amount', amounts }) {
    return `
<div class="desk-invoice">
<style>
.desk-invoice { font-family: Arial, sans-serif; margin: 2px; font-size: 14px; line-height: 1.2; }
.desk-invoice .header { text-align: center; font-size: 22px; font-weight: bold; margin-bottom: 10px; }
.desk-invoice .company-info { margin-bottom: 10px; font-size: 14px; line-height: 1.1; }
.desk-invoice .subject { font-weight: bold; text-decoration: underline; margin: 8px 0; font-size: 14px; }
.desk-invoice table.main { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 11px; }
.desk-invoice table.main th, .desk-invoice table.main td { border: 1px solid black; padding: 4px; text-align: center; word-wrap: break-word; vertical-align: middle; }
.desk-invoice table.main th { background-color: #f0f0f0; font-weight: bold; height: 25px; }
.desk-invoice table.main th:nth-child(1) { width: 6%; }
.desk-invoice table.main th:nth-child(2) { width: 52%; }
.desk-invoice table.main th:nth-child(3), .desk-invoice table.main th:nth-child(4), .desk-invoice table.main th:nth-child(5) { width: 14%; }
.desk-invoice .description { font-size: 11px; padding: 4px 4px 15px 4px; line-height: 1.3; }
.desk-invoice .sum td { padding: 8px 4px; }
.desk-invoice .sum .label { background-color: #e6e6e6; }
.desk-invoice .grand td.label, .desk-invoice .grand td.value { background-color: #bfbfbf; font-weight: bold; }
.desk-invoice .blank { border: none !important; background: white; }
</style>
<div class="header">${escapeHtml(title)}</div>
<table style="width: 100%; margin-bottom: 1px; border: none;">
<tr>
<td style="text-align: left; border: none; padding: 2px;">${escapeHtml(date)}</td>
<td style="text-align: right; border: none; padding: 2px;">Ref: ${escapeHtml(ref)}</td>
</tr>
</table>
<div style="text-align: right; margin-bottom: 8px; font-size: 13px;">TRN#: ${escapeHtml(trn)}</div>
<div class="company-info">
<div>Invygo Tech FZ-LLC</div>
<div>Dubai Internet City</div>
<div>Dubai, U.A.E.</div>
</div>
<div class="subject">${escapeHtml(subject)}</div>
${greeting.map((g) => `<p style="margin: 5px 0;">${escapeHtml(g)}</p>`).join('\n')}
<table class="main">
<tr>
<th>No.</th>
<th>Description</th>
<th>${escapeHtml(amountLabel)}</th>
<th>VAT 5%</th>
<th>Total Price</th>
</tr>
<tr>
<td>1</td>
<td class="description">${description.join('<br>')}</td>
<td>${fix2(amounts.net)}</td>
<td>${fix2(amounts.vat)}</td>
<td>${fix2(amounts.total)}</td>
</tr>
<tr class="sum"><td class="blank" colspan="3"></td><td class="label">Net Amount:</td><td class="value">AED ${fix2(amounts.net)}</td></tr>
<tr class="sum"><td class="blank" colspan="3"></td><td class="label">VAT 5%:</td><td class="value">AED ${fix2(amounts.vat)}</td></tr>
<tr class="sum grand"><td class="blank" colspan="3"></td><td class="label">TOTAL:</td><td class="value">AED ${fix2(amounts.total)}</td></tr>
</table>
<div style="margin-top: 20px;">
<div style="font-weight: bold; text-decoration: underline;">General Conditions:</div>
<br>
<div>Terms of Payment: within 7 days</div>
</div>
<div style="margin-top: 15px;">
<p>Thanking you and assuring you of our best co-operation and services at all times.</p>
<br>
<p>Best Regards,</p>
<br><br>
<p><strong>Saudian Alwefaq Rent A Car</strong></p>
</div>
</div>`;
}

const toDataURL = (url) => fetch(url)
    .then((response) => response.blob())
    .then((blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    }));

/** The invoice HTML on the letterhead, rendered to an A4 PDF and saved as `fileName`. */
export async function saveDeskInvoicePdf(html, fileName) {
    const letterhead = await toDataURL(`${process.env.PUBLIC_URL || ''}/Invoice.jpg`);
    const page = document.createElement('div');
    page.style.cssText = 'position:absolute;left:-9999px;top:0;width:210mm;height:297mm;background:white;';
    const bg = document.createElement('img');
    bg.src = letterhead;
    bg.style.cssText = 'position:absolute;top:0;left:0;width:210mm;height:297mm;z-index:1;';
    const content = document.createElement('div');
    content.style.cssText = 'position:absolute;top:0;left:0;right:0;bottom:0;z-index:2;padding:120px 30px 20px 30px;box-sizing:border-box;';
    content.appendChild(new DOMParser().parseFromString(html, 'text/html').body.firstElementChild);
    page.appendChild(bg);
    page.appendChild(content);
    document.body.appendChild(page);
    try {
        await new Promise((resolve) => setTimeout(resolve, 300));   // let the letterhead image decode
        const canvas = await html2canvas(page, { scale: 2 });
        const pdf = new jsPDF('p', 'mm', 'a4');
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.7), 'JPEG', 0, 0, 210, 297);
        pdf.save(fileName);
    } finally {
        document.body.removeChild(page);
    }
}

/** An ExcelJS workbook downloaded as `fileName`. */
export async function saveWorkbook(workbook, fileName) {
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), fileName);
}

/** The report heading rows both calculators start their Excel with. */
export function addReportHeader(sheet, { title, customer, period }) {
    sheet.addRow([title]);
    sheet.addRow(['Customer:', customer || 'N/A']);
    sheet.addRow(['Overall Period:', period]);
    sheet.addRow([]);
}
